/**
 * De vier copiloot-endpoints uit API-contract §4.
 *
 * De typen staan hier en niet in `@/types/api`, en de aanroepen niet in
 * `@/lib/api.ts`: die twee zijn gedeelde bestanden terwijl vier bouwers
 * tegelijk werken. Wat hier wél uit `@/lib/api.ts` komt is de
 * `apiClient` zelf — importeren is geen wijzigen, en zo houdt de
 * copiloot dezelfde token- en verversingsafhandeling als de rest van
 * het portaal.
 */
import { apiClient } from "@/lib/api";

import type { KijkContext } from "./kijktMee";

/** Eén bron onder een antwoord van de copiloot. */
export interface CopilootKennisbron {
  vraag: string;
  categorie: string;
}

export interface CopilootProductbron {
  naam: string;
  product_url: string | null;
}

export interface CopilootNaslagbron {
  bron_titel: string;
  kop: string | null;
}

/** Eén beurt in de draad. Zelfde vorm als een spar-beurt. */
export interface CopilootBericht {
  id: string;
  rol: "gebruiker" | "sanne";
  inhoud: string;
  concept_tekst: string | null;
  concept_onderwerp: string | null;
  /** Liep tegen het tokenplafond: de tekst blijft staan, "Ga verder" kan. */
  afgekapt: boolean;
  /** De vaste vervolg-instructie achter "Ga verder" — machinetekst. */
  vervolg: boolean;
  kennisbronnen: CopilootKennisbron[];
  productbronnen: CopilootProductbron[];
  naslagbronnen: CopilootNaslagbron[];
  created_at: string;
  /** Sessie 47 (8a): waar de beurt gesteld werd. */
  context_soort?: string | null;
  context_id?: string | null;
  context_label?: string | null;
}

/** Een grijs bericht met de tegenopdracht in de tekst. */
export interface CopilootMelding {
  soort: string;
  moment: string;
  tekst: string;
  chip: string;
  /** Alleen bij een lopende wekker. UTC; hier wordt Amsterdam getoond. */
  afloopt_op: string | null;
}

/** Een regelvoorstel van de leerlaag, met knoppen Ja / Nee. */
export interface CopilootVoorstel {
  id: string;
  titel: string;
  voorstel: string;
  onderbouwing: string | null;
  status: string;
  aangemaakt_op: string;
  open: boolean;
  /** "3 keer gezien, opnieuw gezien op 20-09" (sessie 46, B8). */
  gezien?: string | null;
}

/** Wat opvalt bij het openen van een zaak (sessie 46, B7). */
export interface CopilootOpvallend {
  regel: string;
  voorstel: string;
}

/**
 * De kostenregel met plafondbalk in de kop.
 *
 * `tot_nu_toe_cents` is **alleen de copiloot** — dat is het getal dat
 * tegen het plafond loopt. `alles_cents` is de hele AI-rekening van de
 * maand en remt niets.
 */
export interface CopilootKosten {
  maand: string;
  tot_nu_toe_cents: number;
  maandplafond_cents: number;
  dagplafond_cents: number;
  dag_tot_nu_toe_cents: number;
  maand_vol: boolean;
  deel_gebruikt: number;
  alles_cents?: number;
  sanne_nova_cents?: number;
  overig_cents?: number;
}

export interface CopilootDraad {
  /** Sessie 46 (B6): "Kijkt mee op: <contextregel>". */
  contextregel?: string;
  context_soort?: string;
  context_id?: string | null;
  opvallend?: CopilootOpvallend | null;
  zaak_id?: string;
  email_id?: string | null;
  kan_chatten: boolean;
  /** Waarom de invoer uit staat. Altijd gevuld als er iets uit staat. */
  reden_uit: string | null;
  berichten: CopilootBericht[];
  meldingen: CopilootMelding[];
  voorstellen: CopilootVoorstel[];
  kosten: CopilootKosten;
}

/**
 * Sessie 73 blok 2: een dierveld dat de copiloot op de klantpagina wil
 * zetten. Komt mee in het klaar-event; pas "Doe dat" schrijft het.
 */
export interface DierveldVoorstel {
  huisdier_id: string;
  huisdier_naam: string;
  velden: Record<string, string | number | boolean | { allergeen: string; stand: string }>;
  /** "Gewicht van Max: 28 kg". */
  tekst: string;
  /** "gewicht van Max op 28 kg". */
  gedaan_tekst?: string;
}

/** De knoppen in de invoerrij. `null` is gewoon versturen. */
export type ModelKnop = "fable" | "casetekst" | null;

export const copilootApi = {
  /** Het ene gesprek, met de context van wat er nu in beeld is (B6). */
  async draad(context: KijkContext): Promise<CopilootDraad> {
    const { data } = await apiClient.get<CopilootDraad>(
      "/api/werkplek/copiloot/gesprek",
      { params: { soort: context.soort, id: context.id ?? undefined } },
    );
    return data;
  },

  async kosten(): Promise<CopilootKosten> {
    const { data } = await apiClient.get<CopilootKosten>(
      "/api/werkplek/copiloot/kosten",
    );
    return data;
  },

  async antwoordOpVoorstel(
    voorstelId: string,
    ja: boolean,
  ): Promise<CopilootVoorstel> {
    const { data } = await apiClient.post<{ voorstel: CopilootVoorstel }>(
      `/api/werkplek/copiloot/voorstel/${voorstelId}/antwoord`,
      { ja },
    );
    return data.voorstel;
  },

  /**
   * Zet een dierveld dat de copiloot voorstelde (sessie 73 blok 2). Loopt
   * via de gewone dierroute, met bron "copiloot" voor de wijzigingslog.
   */
  async zetDierveld(voorstel: DierveldVoorstel): Promise<void> {
    // Vermijden gaat per allergeen (de rest van de lijst blijft staan);
    // de andere velden via de gewone dierroute.
    const { vermijden, ...velden } = voorstel.velden;
    if (vermijden && typeof vermijden === "object") {
      await apiClient.post(`/api/huisdieren/${voorstel.huisdier_id}/vermijden`, {
        ...(vermijden as { allergeen: string; stand: string }),
        bron: "copiloot",
      });
    }
    if (Object.keys(velden).length > 0) {
      await apiClient.patch(`/api/huisdieren/${voorstel.huisdier_id}`, {
        ...velden,
        bron: "copiloot",
      });
    }
  },
};

/** De queryKey van de draad; ook de stream schrijft hierin. */
export function copilootQueryKey(context: KijkContext): string[] {
  return ["werkplek", "copiloot", "gesprek", context.soort, context.id ?? ""];
}

/** Euro's uit centen, met een gewone spatie — nooit een harde. */
export function euro(centen: number): string {
  const heel = Math.trunc(Math.abs(centen) / 100);
  const rest = Math.abs(centen) % 100;
  const teken = centen < 0 ? "-" : "";
  return `${teken}€ ${heel},${String(rest).padStart(2, "0")}`;
}

/** "september" — de maand uit "2026-09", voor de kostenregel. */
export function maandNaam(maand: string): string {
  const [jaar, nummer] = maand.split("-").map(Number);
  if (!jaar || !nummer) return maand;
  return new Date(Date.UTC(jaar, nummer - 1, 1)).toLocaleDateString("nl-NL", {
    month: "long",
    timeZone: "UTC",
  });
}
